import { CurrencyPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ServiceReport } from '@models/reports/service/service-report';
import { Tax } from '@models/reports/tax/tax';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { DataSourceRequestState, SortDescriptor, orderBy } from '@progress/kendo-data-query';
import { ReportsService } from '@services/reports.service';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-memberships-reports',
  templateUrl: './memberships-reports.component.html',
  styleUrls: ['./memberships-reports.component.less'],
})
export class MembershipsReportsComponent implements OnInit, OnDestroy {
  @ViewChild('grid') grid;
  gridView: GridDataResult;
  gridState: DataSourceRequestState;
  loading: boolean = false;
  possibleTaxes: Tax[] = [];
  unsub: Subject<void> = new Subject<void>();

  constructor(private reportsService: ReportsService, private currencyPipe: CurrencyPipe) {}

  ngOnInit() {
    this.initDefaultValue();
    this.getPossibleTaxes();
    this.getReportData();
    this.allData = this.allData.bind(this);
  }

  initDefaultValue() {
    this.gridState = {
      skip: 0,
      take: 100,
      // set default state of sorting icon
      sort: [{ field: 'membershipName', dir: 'asc' }],
    };

    this.gridView = {
      data: [],
      total: 0,
    };
  }

  getPossibleTaxes() {
    this.reportsService
      .getMembershipPossibleTaxes()
      .pipe(takeUntil(this.unsub))
      .subscribe((taxes: Tax[]) => {
        taxes.forEach((tax: Tax) => {
          if (!this.possibleTaxes.some((possibleTax: Tax) => possibleTax.id === tax.id)) {
            this.possibleTaxes.push(tax);
          }
        });
      });
  }

  getReportData() {
    this.loading = true;

    this.reportsService.getMembershipsReportEntities(this.gridState).subscribe((membershipReports: any) => {
      this.gridView = {
        data: membershipReports.data,
        total: membershipReports.total || membershipReports.data.length,
      };
      this.loading = false;
    });
  }

  exportToExcel() {
    this.grid.saveAsExcel();
  }

  onExcelExport(e: any) {
    this.reportsService.onExcelExport(e, 'Memberships Report');
  }

  pageChange(gridState: DataStateChangeEvent) {
    this.gridState = gridState;
    this.getReportData();
  }

  sortChange(sort: SortDescriptor[]) {
    this.gridState.sort = sort;
    this.gridView.data = orderBy(this.gridView.data, this.gridState.sort);
  }

  sortIconClass(column: any) {
    return this.gridState.sort.length &&
      this.gridState.sort[0].field === column.field &&
      this.gridState.sort[0].dir === 'asc'
      ? 'fad fa-sort-up'
      : this.gridState.sort.length &&
        this.gridState.sort[0].field === column.field &&
        this.gridState.sort[0].dir === 'desc'
      ? 'fad fa-sort-down'
      : 'fas fa-sort';
  }

  allData = (): Observable<any> => {
    const gridState: DataSourceRequestState = {};
    return this.reportsService.getMembershipsReportEntities(gridState).pipe(
      map((membershipReports: any) => {
        return {
          data: membershipReports.data.map((element: ServiceReport) => {
            this.possibleTaxes.forEach((item) => {
              element[item.name] = this.currencyPipe.transform(element.taxCollected[item.name]);
            });
            return {
              ...element,
              salesAmount: this.currencyPipe.transform(element.salesAmount),
            };
          }),
          total: membershipReports.total,
        };
      })
    );
  };

  ngOnDestroy() {
    this.unsub.next();
    this.unsub.complete();
  }
}
